/**
 * Headless template loader (US 359).
 *
 * There are two templates now, composed from three directories:
 *
 *   headless-template-shared/   the build config, the preview bridge, index.css, cn()
 *   headless-template-web/      + the shadcn kit, react-router-dom, sonner
 *   headless-template-mobile/   + Framework7 and its icon fonts, and nothing web
 *
 * The split exists because a prompt could not hold the line. A mobile screen that imported
 * `useNavigate` compiled perfectly well while react-router was installed, and only failed at
 * render — "useNavigate() may be used only in the context of a <Router>" — because the seeded
 * shell mounts Framework7's `<App>` and `<View>`, not a Router. With the dependency gone the
 * import stops resolving, which is a failure the build can see and repair.
 *
 * Shared files live in one place rather than being copied into both trees: they are the files
 * that have nothing to do with which component library a prototype uses, and duplicating them
 * is how the two drift apart.
 *
 * `getHeadlessTemplateFiles()` keeps its signature and reads the active template, because it is
 * called from a dozen places (write tools, prune, zip download, WebContainer boot) that have no
 * business knowing the mode. `setActiveTemplate` is called once when a project is opened or a
 * build starts; the workbench is single-project, exactly as `workbenchStore` already assumes.
 */

import { atom } from 'nanostores';

export type TemplateId = 'web' | 'mobile';

const SHARED_PREFIX = './headless-template-shared/';
const VARIANT_PREFIX: Record<TemplateId, string> = {
  web: './headless-template-web/',
  mobile: './headless-template-mobile/',
};

const sharedModules: Record<string, string> = import.meta.glob(
  './headless-template-shared/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const webModules: Record<string, string> = import.meta.glob(
  './headless-template-web/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const mobileModules: Record<string, string> = import.meta.glob(
  './headless-template-mobile/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const VARIANT_MODULES: Record<TemplateId, Record<string, string>> = {
  web: webModules,
  mobile: mobileModules,
};

/**
 * Web is the default because every mode except `mobile` uses it, and because a wrong guess there
 * is a prototype with spare dependencies rather than one that cannot resolve its imports.
 */
/**
 * A store, not a variable: the file tree and the editor list the active template's files next to
 * the project's own, and have to redraw when it changes. As a plain variable they kept showing
 * the web template's package.json and src/components/ui on a mobile prototype until something
 * else happened to re-render them (27 Sep 2026).
 */
export const activeTemplateStore = atom<TemplateId>('web');

const _cache = new Map<TemplateId, Record<string, string>>();

function strip(key: string, prefix: string): string {
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}

/** Which template new seeding and template-file lookups use. */
export function getActiveTemplate(): TemplateId {
  return activeTemplateStore.get();
}

/** Set once per project/build. Safe to call repeatedly with the same value. */
export function setActiveTemplate(id: TemplateId): void {
  if (activeTemplateStore.get() !== id) activeTemplateStore.set(id);
}

/**
 * The template a set of project files belongs to.
 *
 * Read from the source as well as `package.json`, the same rule Chef's `templateDirs.ts` uses.
 * `package.json` alone was wrong in practice: the workbench store never holds one - it comes from
 * the active template, which defaults to web - so a mobile prototype resolved as web and the
 * preview loaded the bundle without Framework7 in it. The seeded mobile shell always imports
 * `framework7/bundle`, and nothing in either template does, so that import is the fact to read.
 */
export function templateIdForFiles(files: Record<string, string> | undefined): TemplateId {
  if (!files) return 'web';
  const pkg = files['package.json'];
  if (pkg && /"framework7"\s*:/.test(pkg)) return 'mobile';
  for (const [path, content] of Object.entries(files)) {
    if (!/\.(tsx?|jsx?)$/i.test(path)) continue;
    if (/from\s+['"]framework7(?:\/|-react|['"])/.test(content)) return 'mobile';
  }
  return 'web';
}

/** The template a prototyper mode builds with. */
export function templateIdForMode(mode: string | undefined): TemplateId {
  return mode === 'mobile' ? 'mobile' : 'web';
}

/**
 * Returns `{ "package.json": "...", "src/App.tsx": "...", ... }` for one template: the shared
 * files with the variant's files layered over them. Cached per template after first call.
 */
export function getHeadlessTemplateFiles(
  id: TemplateId = activeTemplateStore.get(),
): Record<string, string> {
  const cached = _cache.get(id);
  if (cached) return cached;

  const files: Record<string, string> = {};
  for (const [key, content] of Object.entries(sharedModules)) {
    files[strip(key, SHARED_PREFIX)] = content;
  }
  for (const [key, content] of Object.entries(VARIANT_MODULES[id])) {
    files[strip(key, VARIANT_PREFIX[id])] = content;
  }

  _cache.set(id, files);
  return files;
}
